import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimate } from "framer-motion";

type CardSpec = {
  id: number;
  img: string;
  x: number;
  y: number;
};

const cards: CardSpec[] = [
  { id: 1, img: "/assets/images/home/pg1.png", x: 102, y: 92 },
  { id: 2, img: "/assets/images/home/pg2.png", x: 158, y: 180 },
  { id: 3, img: "/assets/images/home/pg3.png", x: 108, y: 258 },
  { id: 4, img: "/assets/images/home/pg4.png", x: 412, y: 92 },
  { id: 5, img: "/assets/images/home/pg5.png", x: 350, y: 180 },
  { id: 6, img: "/assets/images/home/pg6.png", x: 412, y: 258 },
];

function pointOnCircle(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function path1(start: { x: number; y: number }, leftX: number, upY: number) {
  const r = 18;
  return `
    M ${start.x} ${start.y}
    L ${start.x} ${upY + r}
    Q ${start.x} ${upY} ${start.x - r} ${upY}
    L ${leftX} ${upY}
  `;
}

function path2(start: { x: number; y: number }, end: { x: number; y: number }) {
  const endPull = 26;
  const ex = end.x - endPull;
  const ey = end.y;

  const mx = (start.x + ex) / 2;
  const my = (start.y + ey) / 2;

  const dx = ex - start.x;
  const dy = ey - start.y;

  const bend = 16;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * bend;
  const ny = (dx / len) * bend;

  const cx = mx + nx;
  const cy = my + ny;

  return `
    M ${start.x} ${start.y}
    Q ${cx} ${cy} ${ex} ${ey}
  `;
}

function path3(start: { x: number; y: number }, leftX: number, downY: number) {
  const r = 18;

  const x1 = start.x - 15;
  const y1 = start.y;

  const xDown = x1 - r;
  const yDownStart = y1;

  const y2 = downY;
  const x2 = xDown;
  const xLeftStart = x2 - r;

  const extraLeft = 80;
  const finalLeftX = leftX - extraLeft;

  return `
    M ${start.x} ${start.y}
    L ${x1} ${y1}
    Q ${x1 - r} ${y1} ${xDown} ${yDownStart}
    L ${x2} ${y2 - r}
    Q ${x2} ${y2} ${xLeftStart} ${y2}
    L ${finalLeftX} ${y2}
  `;
}

function path4(start: { x: number; y: number }, rightX: number, upY: number) {
  const r = 18;
  return `
    M ${start.x} ${start.y + 10}
    L ${start.x} ${upY + r}
    Q ${start.x} ${upY} ${start.x + r} ${upY}
    L ${rightX} ${upY}
  `;
}

function path5(start: { x: number; y: number }, end: { x: number; y: number }) {
  const ctrl = { x: start.x, y: start.y };
  return `
    M ${start.x} ${start.y}
    Q ${ctrl.x} ${ctrl.y - 20} ${end.x + 10} ${end.y}
  `;
}

function path6(start: { x: number; y: number }, end: { x: number; y: number }) {
  const h1 = 55; // 첫 수평 직선 길이
  const r1 = 4; // 첫 코너(살짝 아래로 휘는) 정도
  const v = 22; // 수직으로 내려가는 길이
  const r2 = 18; // 두번째 코너(다시 오른쪽으로 휘는) 정도

  const sx = start.x;
  const sy = start.y - 12;

  // end 쪽으로 붙는 y는 "내려간 y"로 고정
  const xCorner = sx + h1;
  const yDown = sy + v;

  const xEnd = end.x;

  return `
    M ${sx} ${sy}
    L ${xCorner - r1} ${sy}
    Q ${xCorner} ${sy} ${xCorner} ${sy + r1}
    L ${xCorner} ${yDown - r2}
    Q ${xCorner} ${yDown} ${xCorner + r2} ${yDown}
    L ${xEnd + 10} ${yDown}
  `;
}

function bottomLine(start: { x: number; y: number }, end: { x: number; y: number }) {
  return `M ${start.x} ${start.y + 10} L ${end.x} ${end.y}`;
}

export default function HomeHubCards() {
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const ranRef = useRef(false);

  // ======  내부 “기준 캔버스”는 그대로 유지 ======
  const W = 635;
  const H = 604;

  // ======  외곽 스테이지 최대 규격 (요구사항) ======
  const MAX_W = 635;
  const MAX_H = 604;

  // ======  반응형: 부모 폭 측정 ======
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [stageW, setStageW] = useState(MAX_W);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect?.width ?? MAX_W;
      setStageW(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // stage는 aspect(635/604)로 높이가 따라오고,
  // 내부 캔버스(520)는 stage 너비에 맞춰 scale만 줄임
  const scale = useMemo(() => {
    // stageW는 MAX_W 이내로 잡히므로, 내부 캔버스가 커지지 않도록 1로 캡
    const s = stageW / MAX_W;
    return Math.min(s, 1);
  }, [stageW]);

  // 내부 캔버스를 “스테이지 중앙”에 놓고 싶으면 translate로 가운데 정렬
  const offsetX = useMemo(() => (MAX_W - W) / 2, []);
  const offsetY = useMemo(() => (MAX_H - H) / 2, []);

  const logoSize = 92;
  const center = { x: W / 2, y: H / 2 - 30 };
  const radius = logoSize / 2;
  const cardW = 122;

  const attach = useMemo(() => {
    return cards.reduce<Record<number, { x: number; y: number }>>((acc, c) => {
      const isRight = c.x > center.x;

      // 기본: 좌/우 면(현재 로직)
      let ax = isRight ? c.x + 8 : c.x + (cardW - 12);
      let ay = c.y + 28;

      //  3번만: 아래면(가운데)로 연결점 지정

      acc[c.id] = { x: ax, y: ay };
      return acc;
    }, {});
  }, [center.x]);

  const s1 = pointOnCircle(center.x - 10, center.y + 10, radius, -90);
  const s2 = pointOnCircle(center.x + 12, center.y, radius, -135);
  const s3 = pointOnCircle(center.x + 10, center.y + 20, radius, 200);
  const s4 = pointOnCircle(center.x, center.y, radius, -80);
  const s5 = pointOnCircle(center.x - 10, center.y + 15, radius, -55);
  const s6 = pointOnCircle(center.x, center.y - 27, radius, 75);

  const bottomCard = {
    img: "/assets/images/home/card.png",
    x: 170,
    y: 350,
    w: 295,
  };
  const bottomStart = pointOnCircle(center.x, center.y - 10, radius, 90);
  const bottomEnd = { x: bottomCard.x + bottomCard.w / 2, y: bottomCard.y + 6 };

  const lineDefs = useMemo(() => {
    const d1 = path1(s1, attach[1].x, 128);
    const d2 = path2(s2, attach[2]);
    const d3 = path3(s3, attach[3].x, attach[3].y + 10);
    const d4 = path4(s4, attach[4].x + 10, 128);
    const d5 = path5(s5, attach[5]);
    const d6 = path6(s6, attach[6]);
    const db = bottomLine(bottomEnd, bottomStart);

    return [
      { id: 1, d: d1 },
      { id: 2, d: d2 },
      { id: 3, d: d3 },
      { id: 4, d: d4 },
      { id: 5, d: d5 },
      { id: 6, d: d6 },
      { id: 999, d: db },
    ];
  }, [attach, s1, s2, s3, s4, s5, s6, bottomStart.x, bottomStart.y, bottomEnd.x, bottomEnd.y]);

  useEffect(() => {
    let alive = true;
    const el = scope.current;
    if (!el) return;

    const run = async () => {
      if (ranRef.current) return;
      ranRef.current = true;

      // =========================
      // 1) 초기 상태 세팅(모두 숨김)
      // =========================
      await animate(".logo", { opacity: 0, scale: 0.85 }, { duration: 0 });

      for (let i = 1; i <= 6; i++) {
        await animate(`.line-${i}`, { pathLength: 0, opacity: 0 }, { duration: 0 });
        await animate(`.dot-${i}`, { opacity: 0, scale: 0.6 }, { duration: 0 });
        await animate(`.dot-${i}`, { offsetDistance: "0%" } as any, {
          duration: 0,
        });
        await animate(`.card-${i}`, { opacity: 0, y: 10, scale: 0.96 }, { duration: 0 });
      }

      await animate(".bottom-line", { pathLength: 0, opacity: 0 }, { duration: 0 });

      await animate(".bottom-card", { opacity: 0, y: 10, scale: 0.98 }, { duration: 0 });

      if (!alive) return;

      // =========================
      // 2) 재생 순서
      // =========================
      const lineDur = 0.65;
      const cardDur = 0.85;

      //  (1) 맨 아래 카드 이미지 먼저
      await animate(".bottom-card", { opacity: 1, y: 0, scale: 1 }, { duration: 0.65, ease: [0.22, 1, 0.36, 1] });

      if (!alive) return;

      await Promise.all([
        animate(".bottom-line", { pathLength: 1, opacity: 1 }, { duration: 0.52, ease: [0.22, 1, 0.36, 1] }),
        animate(".bottom-dot", { opacity: 1, scale: 1 }, { duration: 0.18, ease: [0.22, 1, 0.36, 1] }),
        animate(".bottom-dot", { offsetDistance: "100%" } as any, {
          duration: 0.52,
          ease: [0.22, 1, 0.36, 1],
        }),
        animate(".logo", { opacity: 1, scale: 1 }, { duration: 0.45, delay: 0.12, ease: [0.22, 1, 0.36, 1] }),
      ]);

      if (!alive) return;

      //  (3) 이후 페어: 최상단 양쪽(1&4) → 중간(2&5) → 최하단(3&6)
      const pairs: Array<[number, number]> = [
        [1, 4],
        [2, 5],
        [3, 6],
      ];

      for (const [a, b] of pairs) {
        if (!alive) return;

        await Promise.all([
          // a
          animate(`.line-${a}`, { pathLength: 1, opacity: 1 }, { duration: lineDur, ease: [0.22, 1, 0.36, 1] }),
          animate(`.dot-${a}`, { opacity: 1, scale: 1 }, { duration: 0.18, ease: [0.22, 1, 0.36, 1] }),
          animate(`.dot-${a}`, { offsetDistance: "100%" } as any, {
            duration: lineDur,
            ease: [0.22, 1, 0.36, 1],
          }),

          // b
          animate(`.line-${b}`, { pathLength: 1, opacity: 1 }, { duration: lineDur, ease: [0.22, 1, 0.36, 1] }),
          animate(`.dot-${b}`, { opacity: 1, scale: 1 }, { duration: 0.18, ease: [0.22, 1, 0.36, 1] }),
          animate(`.dot-${b}`, { offsetDistance: "100%" } as any, {
            duration: lineDur,
            ease: [0.22, 1, 0.36, 1],
          }),
        ]);

        if (!alive) return;

        await Promise.all([
          animate(`.card-${a}`, { opacity: 1, y: 0, scale: 1 }, { duration: cardDur, ease: [0.22, 1, 0.36, 1] }),
          animate(`.card-${b}`, { opacity: 1, y: 0, scale: 1 }, { duration: cardDur, ease: [0.22, 1, 0.36, 1] }),
        ]);
      }
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) run();
      },
      { threshold: 0.45, rootMargin: "0px 0px -10% 0px" }
    );

    io.observe(el);

    return () => {
      alive = false;
      io.disconnect();
    };
  }, [animate, scope]);

  const stroke = "rgba(17,24,39,0.20)";
  const strokeWidth = 2;

  const dotStyle = (d: string) =>
    ({
      position: "absolute",
      width: 7,
      height: 7,
      borderRadius: 999,
      background: "#2563EB",
      offsetPath: `path('${d}')`,
      offsetDistance: "0%",
      offsetRotate: "0deg",
      left: 0,
      top: 0,
      pointerEvents: "none",
    } as React.CSSProperties);

  return (
    //  “스테이지”: w-full로 줄고, 최대 635/604 (비율 유지)
    <div ref={stageRef} className="w-full max-w-[635px]">
      <div
        className="w-full aspect-[635/604] max-h-[604px] rounded-[24px] overflow-hidden bg-[#F6F7FB] relative"
        style={{
          boxShadow: "0 1.813px 19.07px 0 rgba(48, 75, 109, 0.20)",
        }}>
        <img
          src="/assets/images/home/card_bg.png"
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover", // 이미지가 꽉 차게
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
        {/*  내부 캔버스(520x520)는 스케일로만 줄임 */}
        <div
          ref={scope}
          style={{
            width: MAX_W,
            height: MAX_H,
            position: "absolute",
            left: 0,
            top: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}>
          {/* 캔버스 중앙 배치: MAX_W/MAX_H 안에서 520x520을 가운데로 */}
          <div
            style={{
              width: W,
              height: H,
              position: "absolute",
              left: offsetX,
              top: offsetY,
            }}>
            {/* 라인 */}
            <svg width={W} height={H} style={{ position: "absolute", inset: 0 }}>
              {lineDefs
                .filter((l) => l.id !== 999)
                .map((l) => (
                  <motion.path
                    initial={{ pathLength: 0, opacity: 0 }}
                    key={l.id}
                    className={`line-${l.id}`}
                    d={l.d}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                  />
                ))}

              <motion.path
                initial={{ pathLength: 0, opacity: 0 }}
                className="bottom-line"
                d={lineDefs.find((l) => l.id === 999)!.d}
                fill="none"
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
              />
            </svg>

            {/* 중앙 로고 */}
            <motion.img
              initial={{ opacity: 0 }}
              className="logo"
              src="/assets/images/home/cardLogo.png"
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: center.x - logoSize / 2,
                top: center.y - logoSize / 2,
                width: logoSize,
                height: logoSize,
                userSelect: "none",
                filter: "drop-shadow(0 18px 35px rgba(0,0,0,0.10))",
              }}
            />

            {/* PG 카드 1~6 */}
            {cards.map((c) => (
              <motion.img
                initial={{ opacity: 0, y: 12, scale: 0.96 }}
                key={c.id}
                className={`card-${c.id}`}
                src={c.img}
                alt=""
                draggable={false}
                style={{
                  position: "absolute",
                  left: c.x,
                  top: c.y,
                  width: cardW,
                  height: "auto",
                  userSelect: "none",
                  filter: "drop-shadow(0 10px 25px rgba(0,0,0,0.10))",
                }}
              />
            ))}

            {/* 하단 카드 */}
            <motion.img
              initial={{ opacity: 0, y: 12, scale: 0.96 }}
              className="bottom-card"
              src="/assets/images/home/card.png"
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: bottomCard.x,
                top: bottomCard.y,
                width: bottomCard.w,
                height: "auto",
                userSelect: "none",
                filter: "drop-shadow(0 22px 45px rgba(0,0,0,0.12))",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
